import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Select, Avatar, Button, message, Spin, Image, Row, Col, Input, DatePicker, InputNumber, Tabs } from 'antd';
import { DeleteOutlined, VideoCameraOutlined, FileTextOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import apiClient from '../../server/server';
import StoryNewsMediaInput from '../../components/ui/StoryNewsMediaInput';
import { getActiveCountries } from '../../server/configuration/FiltersServer';
import { searchPublicationsMeili } from '../../server/meilisearch/meilisearchServer';
import VideoScraper from './VideoScraper';

const StoryNews = ({ profiles, profilesLoading, searchProfiles }) => {
  const [storyNewsForm] = Form.useForm();
  const [storyNewsLoading, setStoryNewsLoading] = useState(false);
  const [publications, setPublications] = useState([]);
  const [publicationsLoading, setPublicationsLoading] = useState(false);
  const [selectedPublications, setSelectedPublications] = useState([]);
  const [activeStories, setActiveStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [videoUploadRef, setVideoUploadRef] = useState(null);

  // Search state
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Countries and cities state
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);

  // Story News functions
  const fetchPublications = async () => {
    setPublicationsLoading(true);
    try {
      // Use Meilisearch to search for events and tracks
      const response = await searchPublicationsMeili('', { 
        limit: 50,
        isPublished: true 
      });
      
      console.log('Publications Meilisearch response:', response);
      
      // Transform the data to match the expected format
      // The response has results.events.hits and results.tracks.hits
      const events = response.results?.events?.hits || [];
      const tracks = response.results?.tracks?.hits || [];
      
      // Combine and transform events and tracks
      const publicationsData = [
        ...events.map(item => ({
          _id: item.id,
          title: item.title,
          flyer: item.flyer, // Include the flyer field
          publicationData: {
            title: item.title,
            description: item.description,
            publicationType: 'Event',
            flyer: item.flyer, // Include flyer in publicationData too
            // Add other fields as needed
          }
        })),
        ...tracks.map(item => ({
          _id: item.id,
          title: item.title,
          artist: item.artist, // Include the artist field
          flyer: item.flyer, // Include the flyer field
          publicationData: {
            title: item.title,
            description: item.description || '',
            publicationType: 'Track',
            artist: item.artist, // Include artist in publicationData
            flyer: item.flyer, // Include flyer in publicationData too
            // Add other fields as needed
          }
        }))
      ];
      
      console.log('Setting publications to:', publicationsData);
      console.log('Sample publication data structure:', publicationsData[0]);
      setPublications(publicationsData);
    } catch (error) {
      console.error('Error fetching publications:', error);
      message.error('Failed to fetch publications');
      setPublications([]);
    } finally {
      setPublicationsLoading(false);
    }
  };

  const fetchActiveStories = async () => {
    setStoriesLoading(true);
    try {
      const response = await apiClient.get('/story-news/active');
      setActiveStories(response.data || []);
    } catch (error) {
      console.error('Error fetching active stories:', error);
      message.error('Failed to fetch active stories');
      setActiveStories([]);
    } finally {
      setStoriesLoading(false);
    }
  };

  const fetchStoriesByProfile = async (profileId) => {
    if (!profileId) return;
    setStoriesLoading(true);
    try {
      const response = await apiClient.get(`/story-news/profile/${profileId}`);
      setSelectedPublications(response.data || []);
    } catch (error) {
      console.error('Error fetching stories by profile:', error);
      message.error('Failed to fetch stories for this profile');
      setSelectedPublications([]);
    } finally {
      setStoriesLoading(false);
    }
  };

  const handleCreateStoryNews = async (values) => {
    try {
      setStoryNewsLoading(true);
      
      let videoUrl = values.videoUrl || '';
      let thumbnailUrl = values.thumbnailUrl || '';
      
      // Check if videoUrl is the placeholder value
      if (videoUrl === 'pending-upload') {
        videoUrl = ''; // Clear the placeholder
      }
      
      // Upload video files if any pending uploads
      if (videoUploadRef && videoUploadRef.uploadPendingFiles) {
        try {
          console.log('📹 [Video Upload] Uploading pending video files...');
          const uploadedVideoUrls = await videoUploadRef.uploadPendingFiles();
          console.log('📹 [Video Upload] Video files uploaded:', uploadedVideoUrls);
          // Take the first uploaded video URL (assuming single video upload)
          if (uploadedVideoUrls.length > 0) {
            videoUrl = uploadedVideoUrls[0];
          }
        } catch (videoError) {
          console.error('📹 [Video Upload] Failed to upload video files:', videoError);
          message.error('Failed to upload video files. Please try again.');
          setStoryNewsLoading(false);
          return;
        }
      }
      
      // Upload video from URL if it's not from our backend and no file was uploaded
      // Also handle data URLs (previews) by skipping them since they'll be handled by uploadPendingFiles
      if (videoUrl && !videoUrl.includes('api.decadance.app') && !videoUrl.startsWith('https://api.decadance.app/videos/') && !videoUrl.startsWith('data:')) {
        try {
          console.log('📹 [Video Upload] Attempting to upload video from URL:', videoUrl);
          
          const uploadResponse = await apiClient.post('/uploads/from-url', {
            imageUrl: videoUrl // API endpoint accepts imageUrl for any file type
          });
          
          if (uploadResponse.data && uploadResponse.data.data && uploadResponse.data.data.url) {
            videoUrl = uploadResponse.data.data.url;
            console.log('📹 [Video Upload] Successfully got URL:', videoUrl);
          } else {
            throw new Error('Invalid response format from video upload');
          }
        } catch (uploadErr) {
          console.error('📹 [Video Upload] Error:', uploadErr);
          message.error(`Failed to upload video: ${uploadErr.response?.statusText || uploadErr.message}`);
          setStoryNewsLoading(false);
          return;
        }
      }

      // Upload thumbnail if provided and not from our backend
      if (thumbnailUrl && !thumbnailUrl.includes('api.decadance.app') && !thumbnailUrl.startsWith('https://api.decadance.app/images/')) {
        try {
          console.log('🖼️ [Thumbnail Upload] Attempting to upload thumbnail from URL:', thumbnailUrl);
          
          const uploadResponse = await apiClient.post('/uploads/from-url', {
            imageUrl: thumbnailUrl
          });
          
          if (uploadResponse.data && uploadResponse.data.data && uploadResponse.data.data.url) {
            thumbnailUrl = uploadResponse.data.data.url;
            console.log('🖼️ [Thumbnail Upload] Successfully got URL:', thumbnailUrl);
          }
        } catch (uploadErr) {
          console.warn('🖼️ [Thumbnail Upload] Failed, but continuing:', uploadErr);
          // Don't fail the whole process if thumbnail upload fails
        }
      }

      // Ensure we have a valid videoUrl
      if (!videoUrl || videoUrl.trim() === '') {
        message.error('Please upload a video or enter a video URL!');
        setStoryNewsLoading(false);
        return;
      }

      const storyPayload = {
        publication: values.publication || undefined,
        profile: values.profile || undefined,
        cityId: values.city || undefined,
        videoUrl: videoUrl,
        thumbnailUrl: thumbnailUrl || undefined,
        order: values.order || 0,
        expiresAt: values.expiresAt?.toISOString(),
        isActive: values.isActive !== false, // Default to true
        duration: values.duration || 5,
        caption: values.caption || undefined,
        tags: Array.isArray(values.tags) ? values.tags : []
      };

      console.log('📱 [Story News Creation] Payload:', JSON.stringify(storyPayload, null, 2));
      const response = await apiClient.post('/story-news', storyPayload);
      console.log('📱 [Story News Creation] Success Response:', response.data);
      
      message.success('Story news created successfully!');
      storyNewsForm.resetFields();
      setVideoUploadRef(null);
      fetchActiveStories(); // Refresh the active stories list
    } catch (error) {
      console.error('📱 [Story News Creation] Error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      message.error(`Failed to create story news: ${errorMessage}`);
    } finally {
      setStoryNewsLoading(false);
    }
  };

  const handleDeleteStory = async (storyId) => {
    try {
      await apiClient.delete(`/story-news/${storyId}`);
      message.success('Story deleted successfully');
      fetchActiveStories(); // Refresh the list
    } catch (error) {
      console.error('Error deleting story:', error);
      message.error('Failed to delete story');
    }
  };

  // Load countries for story creation
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getActiveCountries();
        if (response && response.data) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error('Failed to load countries:', error);
        message.error('Failed to load countries');
      }
    };
    loadCountries();
  }, []);

  // Handle country change
  const handleCountryChange = (countryId) => {
    const selectedCountry = countries.find(country => country._id === countryId);
    if (selectedCountry) {
      setSelectedCountryId(countryId);
      setCities(selectedCountry.cities || []);
      storyNewsForm.setFieldsValue({ city: null }); // Reset city when country changes
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(async (searchTerm) => {
    if (!searchProfiles) return;
    
    setSearchLoading(true);
    try {
      const results = await searchProfiles(searchTerm);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching profiles:', error);
      message.error('Failed to search profiles');
    } finally {
      setSearchLoading(false);
    }
  }, [searchProfiles]);

  const handleProfileSearch = (value) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If empty search, show initial profiles
    if (!value || value.trim() === '') {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }
    
    // Set loading state immediately
    setSearchLoading(true);
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      debouncedSearch(value.trim());
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
  };

  // Update search results when initial profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0 && searchResults.length === 0) {
      setSearchResults(profiles);
    }
  }, [profiles]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Initialize story news data
  useEffect(() => {
    fetchPublications();
    fetchActiveStories();
  }, []);

  return (
    <div>
      <Tabs
        defaultActiveKey="stories"
        type="card"
        size="large"
        items={[
          {
            key: 'stories',
            label: (
              <span>
                <FileTextOutlined />
                Story News
              </span>
            ),
            children: (
              <Row gutter={24}>
        {/* Story Creation Form */}
        <Col xs={24} lg={12}>
          <Card title="Create Story News" className="mb-4">
            <Form
              form={storyNewsForm}
              layout="vertical"
              onFinish={handleCreateStoryNews}
              initialValues={{
                duration: 5,
                order: 0,
                isActive: true,
                expiresAt: moment().add(24, 'hours')
              }}
            >
              <Form.Item
                name="videoUrl"
                label="Video Upload"
                rules={[
                  { 
                    required: true, 
                    message: 'Please upload a video or enter a video URL!',
                    validator: (_, value) => {
                      // Check if we have pending files to upload
                      if (videoUploadRef && videoUploadRef.hasPendingFiles && videoUploadRef.hasPendingFiles()) {
                        // If we have pending files, validation passes
                        return Promise.resolve();
                      }
                      // Otherwise check for a valid URL
                      if (!value || (typeof value === 'string' && value.trim() === '')) {
                        return Promise.reject(new Error('Please upload a video or enter a video URL!'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <StoryNewsMediaInput
                  maxCount={1}
                  acceptedTypes={['video/*']}
                  value={storyNewsForm.getFieldValue('videoUrl') ? [storyNewsForm.getFieldValue('videoUrl')] : []}
                  onChange={(urls) => {
                    console.log('📹 [Story News Form] Video onChange called with URLs:', urls);
                    // For single video, take the first URL or empty string
                    const videoUrl = urls.length > 0 ? urls[0] : '';
                    console.log('📹 [Story News Form] Setting videoUrl to:', videoUrl);
                    storyNewsForm.setFieldsValue({ videoUrl: videoUrl });
                    // Trigger validation
                    storyNewsForm.validateFields(['videoUrl']);
                  }}
                  onFilesReady={(uploadRef) => {
                    console.log('📹 [Story News Form] Video upload reference received');
                    setVideoUploadRef(uploadRef);
                    // Set a placeholder value to pass validation
                    if (uploadRef && uploadRef.uploadPendingFiles) {
                      storyNewsForm.setFieldsValue({ videoUrl: 'pending-upload' });
                    }
                  }}
                  placeholder="Upload video file or enter video URL"
                />
              </Form.Item>

              <Form.Item
                name="thumbnailUrl"
                label="Thumbnail Upload (Optional)"
              >
                <StoryNewsMediaInput
                  maxCount={1}
                  acceptedTypes={['image/*']}
                  value={storyNewsForm.getFieldValue('thumbnailUrl') ? [storyNewsForm.getFieldValue('thumbnailUrl')] : []}
                  onChange={(urls) => {
                    console.log('🖼️ [Story News Form] Thumbnail onChange called with URLs:', urls);
                    // For single image, take the first URL or empty string
                    const thumbnailUrl = urls.length > 0 ? urls[0] : '';
                    storyNewsForm.setFieldsValue({ thumbnailUrl: thumbnailUrl });
                  }}
                  placeholder="Upload thumbnail image or enter image URL"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="profile"
                    label="Profile (Optional)"
                  >
                    <Select
                      placeholder="Search for a profile"
                      loading={profilesLoading || searchLoading}
                      showSearch
                      allowClear
                      onSearch={handleProfileSearch}
                      onChange={(value) => {
                        if (value) {
                          fetchStoriesByProfile(value);
                        }
                      }}
                      filterOption={false} // Disable client-side filtering since we use server-side search
                      options={searchResults
                        .filter(profile => profile.profileType !== 'ML')
                        .map(profile => ({
                          value: profile._id,
                          label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                src={profile.picture} 
                                size={24} 
                                style={{ marginRight: 8 }}
                              />
                              <span>{profile.username}</span>
                            </div>
                          ),
                        }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="publication"
                    label="Publication (Optional)"
                  >
                    <Select
                      placeholder="Select a publication"
                      loading={publicationsLoading}
                      showSearch
                      allowClear
                      filterOption={(input, option) => {
                        if (!Array.isArray(publications)) return false;
                        const pub = publications.find(p => p._id === option.value);
                        const title = pub?.publicationData?.title || pub?.title || '';
                        return title.toLowerCase().includes(input.toLowerCase());
                      }}
                      options={Array.isArray(publications) ? publications.map(pub => ({
                        value: pub._id,
                        label: pub.publicationData?.title || pub.title || 'Untitled Publication'
                      })) : []}
                      optionRender={(option) => {
                        const pub = publications.find(p => p._id === option.value);
                        if (!pub) return option.label;
                        
                        return (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            padding: '12px',
                            borderBottom: '1px solid #f0f0f0',
                            borderRadius: '8px',
                            margin: '4px 0',
                            backgroundColor: '#fafafa',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}>
                            <div style={{ 
                              width: 60, 
                              height: 60, 
                              borderRadius: '8px',
                              overflow: 'hidden',
                              marginRight: 16,
                              flexShrink: 0,
                              backgroundColor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '2px solid #e8e8e8'
                            }}>
                              {(() => {
                                // For events, use flyer field, for tracks, use coverImage or other fields
                                const imageUrl = pub.publicationData?.flyer || 
                                               pub.publicationData?.coverImage || 
                                               pub.publicationData?.image || 
                                               pub.publicationData?.thumbnail ||
                                               pub.publicationData?.picture ||
                                               pub.publicationData?.media?.[0] ||
                                               pub.flyer ||
                                               pub.coverImage ||
                                               pub.image ||
                                               pub.thumbnail ||
                                               pub.picture ||
                                               pub.media?.[0];
                                
                                console.log('Publication image check:', {
                                  id: pub._id,
                                  title: pub.publicationData?.title,
                                  imageUrl,
                                  publicationData: pub.publicationData,
                                  flyer: pub.flyer
                                });
                                
                                return imageUrl ? (
                                  <img 
                                    src={imageUrl} 
                                    alt={pub.publicationData?.title || pub.title}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                      console.log('Image failed to load:', imageUrl);
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null;
                              })()}
                              <div style={{
                                display: (() => {
                                  const imageUrl = pub.publicationData?.flyer || 
                                                 pub.publicationData?.coverImage || 
                                                 pub.publicationData?.image || 
                                                 pub.publicationData?.thumbnail ||
                                                 pub.publicationData?.picture ||
                                                 pub.publicationData?.media?.[0] ||
                                                 pub.flyer ||
                                                 pub.coverImage ||
                                                 pub.image ||
                                                 pub.thumbnail ||
                                                 pub.picture ||
                                                 pub.media?.[0];
                                  return imageUrl ? 'none' : 'flex';
                                })(),
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: pub.publicationData?.publicationType === 'Event' ? '#1890ff' : '#52c41a',
                                color: 'white',
                                fontSize: '20px',
                                fontWeight: 'bold'
                              }}>
                                {pub.publicationData?.publicationType === 'Event' ? '🎵' : '🎧'}
                              </div>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 'bold', 
                                fontSize: '16px',
                                color: '#262626',
                                marginBottom: '4px',
                                lineHeight: '1.2',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {pub.publicationData?.title || pub.title || 'Untitled Publication'}
                              </div>
                              {pub.publicationData?.publicationType === 'Track' && pub.publicationData?.artist && (
                                <div style={{ 
                                  fontSize: '13px',
                                  color: '#8c8c8c',
                                  marginBottom: '4px',
                                  fontStyle: 'italic'
                                }}>
                                  by {pub.publicationData.artist}
                                </div>
                              )}
                              <div style={{ 
                                fontSize: '13px',
                                color: '#8c8c8c',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap'
                              }}>
                                <span style={{
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  backgroundColor: pub.publicationData?.publicationType === 'Event' ? '#e6f7ff' : '#f6ffed',
                                  color: pub.publicationData?.publicationType === 'Event' ? '#1890ff' : '#52c41a',
                                  border: `1px solid ${pub.publicationData?.publicationType === 'Event' ? '#91d5ff' : '#b7eb8f'}`
                                }}>
                                  {pub.publicationData?.publicationType || 'Publication'}
                                </span>
                                {pub.publicationData?.description && (
                                  <span style={{ 
                                    fontSize: '12px',
                                    color: '#8c8c8c',
                                    lineHeight: '1.3'
                                  }}>
                                    {pub.publicationData.description.length > 60 
                                      ? `${pub.publicationData.description.substring(0, 60)}...` 
                                      : pub.publicationData.description}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="country"
                    label="Country (Optional)"
                  >
                    <Select
                      placeholder="Select country"
                      onChange={handleCountryChange}
                      showSearch
                      allowClear
                      filterOption={(input, option) => {
                        const country = countries.find(c => c._id === option.value);
                        return country?.name?.toLowerCase().includes(input.toLowerCase());
                      }}
                    >
                      {countries.map(country => (
                        <Select.Option key={country._id} value={country._id}>
                          {country.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="city"
                    label="City (Optional)"
                  >
                    <Select 
                      placeholder="Select city" 
                      showSearch
                      allowClear
                      filterOption={(input, option) => {
                        const city = cities.find(c => c._id === option.value);
                        return city?.name?.toLowerCase().includes(input.toLowerCase());
                      }}
                    >
                      {cities.map(city => (
                        <Select.Option key={city._id} value={city._id}>
                          {city.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="duration"
                    label="Duration (seconds)"
                    rules={[{ required: true, message: 'Please enter duration!' }]}
                  >
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="order"
                    label="Order"
                    rules={[{ required: true, message: 'Please enter order!' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="expiresAt"
                label="Expires At"
                rules={[{ required: true, message: 'Please select expiration date!' }]}
              >
                <DatePicker 
                  showTime 
                  format="YYYY-MM-DD HH:mm:ss" 
                  className="w-full" 
                />
              </Form.Item>

              <Form.Item
                name="caption"
                label="Caption (Optional)"
              >
                <Input.TextArea rows={3} placeholder="Enter story caption..." />
              </Form.Item>

              <Form.Item
                name="tags"
                label="Tags (Optional)"
              >
                <Select mode="tags" placeholder="Add tags..." />
              </Form.Item>

              <Form.Item name="isActive" valuePropName="checked">
                <Input type="checkbox" /> Active
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={storyNewsLoading}>
                  Create Story News
                </Button>
                <Button 
                  className="ml-2" 
                  onClick={() => {
                    storyNewsForm.resetFields();
                    setVideoUploadRef(null);
                  }}
                >
                  Reset
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Active Stories List */}
        <Col xs={24} lg={12}>
          <Card 
            title="Active Stories" 
            className="mb-4"
            extra={
              <Button 
                onClick={fetchActiveStories}
                loading={storiesLoading}
                size="small"
              >
                Refresh
              </Button>
            }
          >
            {storiesLoading ? (
              <div className="text-center">
                <Spin size="large" />
                <p className="mt-2">Loading active stories...</p>
              </div>
            ) : activeStories.length > 0 ? (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {activeStories.map((story) => (
                  <Card
                    key={story._id}
                    size="small"
                    className="mb-2"
                    actions={[
                      <Button 
                        key="delete"
                        danger 
                        size="small" 
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteStory(story._id)}
                      >
                        Delete
                      </Button>
                    ]}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {story.thumbnailUrl && (
                        <Image
                          src={story.thumbnailUrl}
                          alt="Thumbnail"
                          width={60}
                          height={60}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                          preview={false}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          Order: {story.order} | Duration: {story.duration}s
                        </div>
                        {story.caption && (
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            {story.caption}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: '#999' }}>
                          Expires: {moment(story.expiresAt).format('YYYY-MM-DD HH:mm')}
                        </div>
                        {story.tags && story.tags.length > 0 && (
                          <div style={{ fontSize: 11, color: '#1890ff', marginTop: 4 }}>
                            Tags: {story.tags.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center mt-4">
                <p>No active stories found.</p>
              </div>
            )}
          </Card>
        </Col>
              </Row>
            ),
          },
          {
            key: 'video-scraper',
            label: (
              <span>
                <VideoCameraOutlined />
                Video Scraper
              </span>
            ),
            children: <VideoScraper />,
          },
        ]}
      />
    </div>
  );
};

export default StoryNews; 